/*
  Central Bank of India QR card for the NEW mold (CBINewQRCard) - printed at 84.3mm x 133mm.
  Same elements as the old-mold card (CBIQRCard), re-proportioned for the shorter
  new-mold page and using the SVG header/footer artwork:
    - /assets/CBI/cbi-logo-top.svg          (bank logo + tagline strip)
    - /assets/CBI/cbi-logo-group-bottom.svg (BHIM/UPI + accepted-app logos)
  Sizes are tuned for the 380 x 600 px capture BulkQRDialog renders this card at.
*/
import React, { useRef } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import QRCodeGenerator from './QRCodeGenerator';
import FitMerchantName from './FitMerchantName';

export default function CbiNewScanAndPayCard({ qrString, merchantVpa, merchantName }) {
  const cardRef = useRef(null);

  return (
    <Box
      ref={cardRef}
      sx={{
        width: '100%',
        height: '100%',
        padding: '9px',
        backgroundColor: '#255ba8',
        borderRadius: '34px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}
    >
      {/* Inner Red Border */}
      <Box
        sx={{
          padding: '7px',
          backgroundColor: '#ad1f24',
          borderRadius: '28px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          boxSizing: 'border-box'
        }}
      >
        {/* White Content Area */}
        <Box
          sx={{
            padding: '16px 14px 14px',
            backgroundColor: '#ffffff',
            borderRadius: '22px',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            overflow: 'hidden',
            minHeight: 0,
            boxSizing: 'border-box'
          }}
        >
          {/* Header - bank logo with the "CENTRAL TO YOU SINCE 1911" strip */}
          <Box
            component="img"
            src="/assets/CBI/cbi-logo-top.svg"
            alt="Central Bank of India"
            sx={{ width: '80%', height: 'auto', flexShrink: 0, display: 'block' }}
          />

          {/* Merchant Name */}
          <FitMerchantName
            text={merchantName}
            maxFontSize={15}
            minFontSize={10}
            maxLines={2}
            lineHeight={1.1}
            textTransform="uppercase"
            containerSx={{ width: '100%', mt: 1 }}
          />

          {/* Scan & Pay - sits straight under the name: the tight line-height
              drops Typography's default leading and the negative margin absorbs
              the descender buffer FitMerchantName adds below its text. */}
          <Typography
            align="center"
            sx={{
              color: '#1a88c9',
              fontWeight: 700,
              fontSize: 24,
              lineHeight: 1.1,
              flexShrink: 0,
              mb: 2,
              mt: '-6px',
              fontFamily: 'Montserrat'
            }}
          >
            Scan & Pay
          </Typography>

          {/* QR Code */}
          <Box
            sx={{
              mt: 1,
              p: 1.2,
              border: '1px solid #9e9e9e',
              borderRadius: '14px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexShrink: 0
            }}
          >
            <QRCodeGenerator qrString={qrString} size={208} />
          </Box>

          {/* UPI ID */}
          <Typography
            align="center"
            sx={{
              mt: 1,
              color: 'black',
              fontWeight: 600,
              fontFamily: 'Montserrat',
              fontSize: 13,
              wordBreak: 'break-all',
              flexShrink: 0
            }}
          >
            {merchantVpa}
          </Typography>

          <Divider
            sx={{
              mt: 1,
              backgroundColor: 'grey',
              height: '1.5px',
              width: '90%',
              flexShrink: 0
            }}
          />

          {/* Footer - BHIM/UPI and the accepted payment apps */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mt: 2
            }}
          >
            <Box
              component="img"
              src="/assets/CBI/cbi-logo-group-bottom.svg"
              alt="BHIM UPI accepted here"
              sx={{
                width: '74%',
                maxHeight: '100%',
                height: 'auto',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
